import CloseIcon from "@mui/icons-material/Close";
import { Backup } from "@mui/icons-material";
import { Button, CircularProgress, Dialog, Typography } from "@mui/material";
import { ChangeEvent, FormEvent, useState } from "react";
import { toast } from "react-toastify";
import { SignerType } from "../../common/TravelDocument";
import { useUploadTravelDocumentMutation } from "../../redux/api/travelDocument";

interface TravelDocumentsUploadModalProps {
  jobOrderId: number;
  signer: SignerType;
  onUpload: Function;
}

const TravelDocumentsUploadModal = (props: TravelDocumentsUploadModalProps) => {
  const [file, setFile] = useState<File>();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadTravelDocument] = useUploadTravelDocumentMutation();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const mb = 1024 * 1024; // size of a MB in Bytes
    if (!event.target.files || !event.target.files[0]) {
      setFile(undefined);
      return;
    }
    if (event.target.files[0].size > 20 * mb) {
      toast.error(<p>File is too big. Files must be 20MB or less.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    setFile(event.target.files[0]);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // check that required values are present
    if (!file) {
      toast.error(<p>No valid file to Upload.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    uploadDocument(file, JSON.stringify({ signerType: props.signer }));
  };

  const uploadDocument = async (file: File, body: string) => {
    setUploading(true);
    // format file and body as Form Data
    const data = new FormData();
    data.append("file", file);
    data.append(
      "travelDocument",
      new Blob([body], { type: "application/json" })
    );
    const response = await uploadTravelDocument({
      jobOrderId: props.jobOrderId.toString(),
      data,
    });
    setUploading(false);

    if ("error" in response) {
      // TODO: error surfacing, success feedback
    } else {
      props.onUpload();
      handleClose();
    }
  };

  return (
    <>
      <Button className="blue-link-button" onClick={handleOpen}>
        <Backup />
        Upload Document
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        className="modal"
      >
        <div className="modal-header mb-2">
          <Typography id="modal-title" variant="h3">
            Upload Document
          </Typography>
          <Button onClick={() => setOpen(false)} className="grey-link-button">
            <CloseIcon />
          </Button>
        </div>
        <form onSubmit={handleSubmit}>
          <label htmlFor="fileUpload">
            <span>Upload File:</span>
            <input type="file" id="fileUpload" onChange={handleFileChange} />
          </label>
          <div className="modal-button">
            <Button
              className="outline-button-small"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <input
              className="blue-button-small"
              type="submit"
              value="Upload Document"
              disabled={uploading}
            />
            {uploading && <CircularProgress />}
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default TravelDocumentsUploadModal;
